import type React from 'react'
import type { BattleInfo, BattleSnapshot, GoalState } from '@boss/shared'
import { scene, stageIndex } from '../scene/config'
import { Countdown } from './Countdown'

const fmt = new Intl.NumberFormat('en-US')

function tone(progress: number) {
  const left = 1 - Math.max(0, Math.min(1, progress))
  return progress >= 1 ? 'ko' : left <= 0.2 ? 'low' : left <= 0.5 ? 'mid' : 'high'
}

function Track({ progress, label }: { progress: number; label: string }) {
  const left = (1 - Math.max(0, Math.min(1, progress))) * 100
  return (
    <div className={`hp-track-wrap tone-${tone(progress)}`}>
      <span className="hp-tag" aria-hidden="true">HP</span>
      <div className="hp-track" role="progressbar" aria-label={`${label} health`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(left)}>
        <div className="hp-fill" style={{ width: `${left}%` }} />
      </div>
    </div>
  )
}

function GoalRow({ goal }: { goal: GoalState }) {
  const remaining = Math.max(0, goal.target - goal.points)
  return (
    <div className="hp-row" style={goal.color ? ({ '--goal': goal.color } as React.CSSProperties) : undefined}>
      <Track progress={goal.progress} label={goal.label} />
      <span className="hp-meta">
        <span className="hp-meta-label" style={goal.color ? { color: goal.color } : undefined}>{goal.label}</span>
        <span className="hp-meta-val">{goal.complete ? 'KO' : `${fmt.format(remaining)} / ${fmt.format(goal.target)}`}</span>
      </span>
    </div>
  )
}

const Skull = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" fill="currentColor">
    <path d="M12 2a9 9 0 0 0-9 9c0 3.1 1.6 5.8 4 7.4V21a1 1 0 0 0 1 1h2v-2h2v2h2v-2h2v2h2a1 1 0 0 0 1-1v-2.6c2.4-1.6 4-4.3 4-7.4a9 9 0 0 0-9-9zm-3.5 12a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm7 0a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zM12 17l-1.5-3h3z" />
  </svg>
)

/** Replaces the goal rows once the boss is down. */
function Defeated({ goals, title, phases, note }: { goals: GoalState[]; title: string; phases: string; note: string }) {
  return (
    <div className="hp-defeated">
      <p className="hp-defeated-banner">
        <Skull />
        <span>{title}</span>
      </p>
      <p className="hp-defeated-phases">
        <span>{phases}</span>
        <span className="hp-phase-gems" aria-label={`${goals.length} of ${goals.length} phases`}>
          {goals.map((g) => (
            <i key={g.id} className={`hp-gem${g.complete ? ' on' : ''}`} />
          ))}
        </span>
      </p>
      {note ? <p className="hp-defeated-note">{note}</p> : null}
    </div>
  )
}

/**
 * The classic battle box: name and level on top, the boss's HP bar, then one
 * smaller HP row per goal. Every bar drains as its packs get opened. Once the
 * boss is down the goal rows give way to the "raid boss defeated" panel.
 */
export function Hud({ snapshot, level, window: win, timeLeftNote, defeated, className = '' }: { snapshot: BattleSnapshot | null; level?: string; window?: BattleInfo['window']; timeLeftNote?: string; defeated?: { title: string; phases: string; note: string }; className?: string }) {
  if (!snapshot) return <aside className={`hud hud-loading ${className}`}>Loading battle…</aside>
  const healthPct = (100 - snapshot.progress * 100).toFixed(2)
  const stage = snapshot.defeated ? scene.stages.length + 1 : stageIndex(snapshot.progress) + 1
  const lv = level && level.trim() ? level : String(stage)
  return (
    <aside className={`hud ${className}`} aria-label="Boss health">
      <div className="hpbox-outer">
        <div className="hpbox">
          <div className="hpbox-head">
            <h1 className="hpbox-name">{snapshot.name}</h1>
            <span className="hpbox-level">
              Lv.<b>{lv}</b>
            </span>
          </div>
          <div className="hp-row hp-main">
            <Track progress={snapshot.progress} label="Boss" />
          </div>
          <p className={`hud-status${snapshot.defeated ? ' defeated' : ''}`}>
            {snapshot.defeated ? '0.00% HP · fainted' : `${healthPct}% HP · stage ${stage}/${scene.stages.length}`}
          </p>
          {win ? <Countdown startsAt={win.startsAt} endsAt={win.endsAt} defeated={snapshot.defeated} note={timeLeftNote} /> : null}
          {snapshot.defeated ? (
            <Defeated
              goals={snapshot.goals}
              title={defeated?.title ?? 'Raid boss defeated'}
              phases={defeated?.phases ?? 'All phases cleared'}
              note={defeated?.note ?? ''}
            />
          ) : (
            <div className="hud-goals">
              {snapshot.goals.map((g) => (
                <GoalRow key={g.id} goal={g} />
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}
